import { useCallback, useState } from 'react';
import type { RefObject } from 'react';
import { getSearchPredictionSortOptions, sortSearchPredictions } from 'components/suggested-location-dropdown';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import type SearchPrediction from 'data/search-predictions';

export type UseLocationSearchBarOptions = {
  areaName?: string;
  onLocationSearchClick: (searchPrediction: SearchPrediction) => void;
};

export type UseLocationSearchBarResult = {
  locationQuery: string;
  setLocationQuery: (query: string) => void;
  searchBarPlaceholder: string | undefined;
  isLocationDropdownActive: boolean;
  setIsLocationDropdownActive: (isActive: boolean) => void;
  handleLocationSearchClick: (searchPrediction: SearchPrediction) => void;
  handleSearchIconClick: (searchPredictions?: SearchPrediction[]) => void;
  searchContainerRef: RefObject<HTMLDivElement | null>;
};

/**
 * Shared state/handlers for the area-page-v2 location search bar (the pill with the
 * search icon + `LocationSearch`). Used by both the desktop `TopBar` and the mobile
 * top bar so the two stay behaviorally identical.
 */
export function useLocationSearchBar({
  areaName,
  onLocationSearchClick,
}: UseLocationSearchBarOptions): UseLocationSearchBarResult {
  const [locationQuery, setLocationQuery] = useState(areaName ?? '');
  const [searchBarPlaceholder, setSearchBarPlaceholder] = useState(
    () => areaName?.split(',')[0]
  );
  const [isLocationDropdownActive, setIsLocationDropdownActive] = useState(false);
  const [syncedAreaName, setSyncedAreaName] = useState(areaName);

  if (areaName !== syncedAreaName) {
    setSyncedAreaName(areaName);
    if (areaName) {
      setLocationQuery(areaName);
      setSearchBarPlaceholder(areaName.split(',')[0]);
    }
  }

  const handleLocationSearchClick = useCallback((searchPrediction: SearchPrediction) => {
    setSearchBarPlaceholder(searchPrediction.description.split(',')[0]);
    setLocationQuery(searchPrediction.description);
    setIsLocationDropdownActive(false);
    onLocationSearchClick(searchPrediction);
  }, [onLocationSearchClick]);

  const handleSearchIconClick = useCallback((searchPredictions?: SearchPrediction[]) => {
    if (locationQuery.length < 3 || !searchPredictions?.length) return;
    const sortOptions = getSearchPredictionSortOptions(locationQuery, searchPredictions);
    const suggestedResults = sortSearchPredictions(searchPredictions, sortOptions);
    const prioritizedGroupResults = sortOptions.prioritizeListings
      ? [...suggestedResults.listings, ...suggestedResults.locations]
      : [...suggestedResults.locations, ...suggestedResults.listings];
    const flattenedResults = [
      ...suggestedResults.militaryBases,
      ...prioritizedGroupResults,
      ...suggestedResults.schools,
    ];
    if (flattenedResults[0]) {
      handleLocationSearchClick(flattenedResults[0]);
    }
  }, [handleLocationSearchClick, locationQuery]);

  const searchContainerRef = useOutsideClickHandler<HTMLDivElement>(
    () => setIsLocationDropdownActive(false)
  );

  return {
    locationQuery,
    setLocationQuery,
    searchBarPlaceholder,
    isLocationDropdownActive,
    setIsLocationDropdownActive,
    handleLocationSearchClick,
    handleSearchIconClick,
    searchContainerRef,
  };
}
